import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { AnalyticsParams, WorkspaceDTO } from '../interfaces';
import { CampaignSearchParams } from '../interfaces/workspace-search-params.interface';
import { sortWorkspacesByName } from '../utils/workspace-sort.utils';
import { AuthUserUpdatedEvent } from '../models/events';
import { ApiRequestOptions, ApiService } from './api.service';
import { EmitterService } from './emitter.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  authUser: any;
  isHrUser: boolean = false;
  isYslUser: boolean = false;
  private readonly allSubjectsEmitted = false;
  selectedBrand: BehaviorSubject<any> = new BehaviorSubject(false);
  selectedComponent = new BehaviorSubject<any>('');
  private readonly brandsCache = new BehaviorSubject<any[]>([]);
  private readonly brandByIdCache = new BehaviorSubject<Record<string, any>>({});
  // // brands
  // public selectedBrands = new BehaviorSubject([]);
  // public selectedBrandsData = this.selectedBrands.asObservable();
  // //workspaces
  // public selectedWorkspaces = new BehaviorSubject([]);
  // public selectedWorkspacesData = this.selectedWorkspaces.asObservable();
  // // projects
  // public selectedProjects = new BehaviorSubject([]);
  // public selectedProjectsData = this.selectedProjects.asObservable();
  // // priorities
  // public selectedPriority = new BehaviorSubject([]);
  // public selectedPriorityData = this.selectedProjects.asObservable();
  selectedBrands = new BehaviorSubject<any[]>([]);
  selectedWorkspaces = new BehaviorSubject<any[]>([]);
  selectedProjects = new BehaviorSubject<any[]>([]);
  selectedCampaigns = new BehaviorSubject<any[]>([]);
  selectedPriority = new BehaviorSubject<any[]>([]);
  selectedFromDate = new BehaviorSubject<any>('');
  selectedToDate = new BehaviorSubject<any>('');
  selectedMilestones = new BehaviorSubject<any[]>([]);
  selectedAllMilestones = new BehaviorSubject<boolean>(true);
  selectedAllBrands = new BehaviorSubject<boolean>(true);
  selectedAllCampaigns = new BehaviorSubject<boolean>(true);
  selectedAllWorkspaces = new BehaviorSubject<boolean>(true);

  // Combine BehaviorSubjects and map to return only id for brands, workspaces, and projects
  allData$: Observable<{
    brands: any[] | any;
    workspaces: any[] | any;
    driveCamapigns: any[] | any;
    campaigns: any[] | any;
    priority: any[];
    fromDate: any[] | any;
    toDate: any[] | any;
    milestones: any[] | any;
    allBrands: boolean;
    allWorkspaces: boolean;
    allCampaigns: boolean;
    allMilestones: boolean;
  }> = combineLatest([
    this.selectedBrands.asObservable(),
    this.selectedWorkspaces.asObservable(),
    this.selectedProjects.asObservable(),
    this.selectedCampaigns.asObservable(),
    this.selectedPriority.asObservable(),
    this.selectedFromDate.asObservable(),
    this.selectedToDate.asObservable(),
    this.selectedMilestones.asObservable(),
    this.selectedAllBrands.asObservable(),
    this.selectedAllWorkspaces.asObservable(),
    this.selectedAllCampaigns.asObservable(),
    this.selectedAllMilestones.asObservable(),
  ]).pipe(
    map(
      ([
        brands,
        workspaces,
        driveCamapigns,
        campaigns,
        priority,
        fromDate,
        toDate,
        milestone,
        allBrands,
        allWorkspaces,
        allCampaigns,
        allMilestones,
      ]) => ({
        brands:
          (Array.isArray(brands) ? brands.map((item: any) => item?.id || null) : brands && [(brands as any).id]) ||
          null,
        workspaces:
          (Array.isArray(workspaces)
            ? workspaces.map((item: any) => item?.id || null)
            : workspaces && (workspaces as any).id) || null,
        driveCamapigns:
          (Array.isArray(driveCamapigns)
            ? driveCamapigns.map((item: any) => item?.id || null)
            : driveCamapigns && (driveCamapigns as any).id) || null,
        campaigns:
          (Array.isArray(campaigns)
            ? campaigns.map((item: any) => item?.id || null)
            : campaigns && (campaigns as any).id) || null,
        milestones:
          (Array.isArray(milestone)
            ? milestone.map((item: any) => item?.id || null)
            : milestone && (milestone as any).id) || null,
        priority,
        fromDate,
        toDate,
        allBrands,
        allWorkspaces,
        allCampaigns,
        allMilestones,
      })
    )
  );

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService
  ) {
    EmitterService.get(AuthUserUpdatedEvent).subscribe((user) => {
      this.clearAnalyticsCache();
    });
  }

  createBrand(data) {
    return this.apiService.httpPost('/api/brands', data);
  }

  updateBrand(data) {
    return this.apiService.httpPost(`/api/brands/${data.id}`, data);
  }

  getAllBrands() {
    return this.apiService.httpGet('/api/brands/all');
  }

  getBrands() {
    if (this.brandsCache.value.length > 0) {
      return of(this.brandsCache.value);
    }

    return this.apiService.httpGet('/api/brands').pipe(
      map((response: any) => response as any[]),
      tap((brands) => {
        this.brandsCache.next(brands);
      })
    );
  }

  /** Full workspaces of one brand; GET /api/brands only lists their ids. */
  getBrandWorkspaces(
    brandId: string,
    companyId: string = this.authService.getAuthUser()?.companyId,
    options: ApiRequestOptions = {}
  ): Observable<WorkspaceDTO[]> {
    return this.apiService
      .httpGet(`/api/workspace/${companyId}?brandId=${brandId}`, false, {}, options)
      .pipe(map(sortWorkspacesByName));
  }

  /** Workspaces of the active brand: the stored selection, then the user's default brand, then the first brand. */
  getActiveBrandWorkspaces(): Observable<WorkspaceDTO[]> {
    const brandId = this.getUserBrandId();
    const brandId$ =
      brandId && brandId !== 'null' ? of(brandId) : this.getBrands().pipe(map((brands) => brands[0]?.id));
    return brandId$.pipe(switchMap((id) => (id ? this.getBrandWorkspaces(id) : of([]))));
  }

  getBrandsByCompanyId(companyId) {
    return this.apiService.httpGet(`/api/brands/${companyId}`);
  }
  getBrandsByCompany() {
    return this.apiService.httpGet(`/api/brands/${this.authUser.companyId}`);
  }

  getBrandsById(brandId: string) {
    const id = `${this.authUser.companyId}/${brandId}`;
    if (this.brandByIdCache.value[id]) {
      return of(this.brandByIdCache.value[id]);
    }

    return this.apiService.httpGet(`/api/brands/${id}`).pipe(
      tap((brand) => {
        const currentCache = this.brandByIdCache.value;
        this.brandByIdCache.next({
          ...currentCache,
          [brandId]: brand,
        });
      })
    );
  }

  private getUserBrandId(): string {
    let brandId: string | null = localStorage.getItem('brandId');
    if (!brandId) {
      brandId = this.authService.getAuthUser()?.brandId;
    }
    return brandId || '';
  }

  getFavoriteItem(type: string, searchParams?: CampaignSearchParams): Observable<any> {
    const brandId = this.getUserBrandId();

    if (brandId && brandId != 'null' && brandId != '') {
      let urlParams = [],
        url = `/api/brand-favorite?brandId=${brandId}&type=${type}`;

      if (searchParams?.activeProduct?.length) {
        urlParams.push(`activeProduct=${searchParams.activeProduct}`);
      }
      if (searchParams?.selectedMonths?.length) {
        urlParams.push(`selectedMonths=${searchParams.selectedMonths.join(',')}`);
      }
      if (searchParams?.projectLeaders?.length) {
        urlParams.push(`projectLeaders=${encodeURIComponent(searchParams.projectLeaders.join(','))}`);
      }
      if (searchParams?.cncManagers?.length) {
        urlParams.push(`cncManagers=${encodeURIComponent(searchParams.cncManagers.join(','))}`);
      }
      if (urlParams.length) {
        url += '&' + urlParams.join('&');
      }
      return this.apiService.httpGet(url);
    } else {
      // Return an empty observable if brandId is missing to avoid undefined errors
      return of([]); // Assuming `of` from `rxjs` to return an empty array
    }
  }

  getFavoriteItemByCampaignId(type: string, campaignId: string) {
    const brandId: string = this.getUserBrandId();
    return this.apiService.httpGet(`/api/brand-favorite?brandId=${brandId}&type=${type}&campaignId=${campaignId}`);
  }

  markAsFavorite(item: any, type: string) {
    const brandId: string = this.getUserBrandId();
    const itemsId = [];
    if (item) {
      itemsId.push(item.id);
    }
    return this.apiService.httpPostWithoutBody(`/api/brand-favorite?brandId=${brandId}&items=${itemsId}&type=${type}`);
  }

  removeAsFavorite(item: any, type: string) {
    const brandId: string = this.getUserBrandId();
    const itemsId = [];
    if (item) {
      itemsId.push(item.id);
    }
    return this.apiService.httpDelete(`/api/brand-favorite?brandId=${brandId}&items=${itemsId}&type=${type}`);
  }

  /** Fetches ONE page (USER_PROJECTS_PAGE_SIZE items, 1-based) — the caller pages via infinite scroll. */
  public getProjectsUser(
    user_name: any,
    isDriveCampaign?: boolean,
    workspaceId?: string,
    standAloneProjects?: boolean,
    page: number = 1
  ): Observable<any> {
    const urlParams = [`page=${page}`];
    if (workspaceId) {
      urlParams.push(`workspaceId=${workspaceId}`);
    }
    if (!standAloneProjects) {
      urlParams.push(`isDriveCampaign=${isDriveCampaign}`);
    }
    return this.apiService.httpGet(`/api/projects/users/${user_name}?${urlParams.join('&')}`);
  }

  // public getStandaloneProjectInWorkspace(workspaceId: string): Observable<any> {
  //   return this.apiService.httpGet(`/api/projects/by-workspace/${workspaceId}?standAloneProject=true`);
  // }

  // public getProjectsByCampaignId(campaignId: string): Observable<any> {
  //   let url = `/api/projects/by-campaign/${campaignId}`;
  //   return this.apiService.httpGet(url);
  // }
  public getStandaloneProjectInWorkspace(workspaceId: string, isStandAlone: boolean, dates?: any): Observable<any> {
    let url = `/api/projects/by-workspace`;
    const urlParams = [];
    if (workspaceId) {
      urlParams.push(`workspaceIds=${workspaceId}`);
    }
    if (isStandAlone) {
      urlParams.push(`standAloneProject=${isStandAlone}`);
    }
    if (dates) {
      urlParams.push(`startDate=${dates.startDate}`);
      urlParams.push(`endDate=${dates.endDate}`);
    }
    if (urlParams.length > 0) {
      url += '?' + urlParams.join('&');
    }
    return this.apiService.httpGet(url);
  }

  public getProjectsByCampaignId(campaignId: string, dates?: any): Observable<any> {
    let url = `/api/projects/by-campaign`;
    const urlParams = [];
    if (campaignId) {
      urlParams.push(`campaignIds=${campaignId}`);
    }
    if (dates) {
      urlParams.push(`startDate=${dates.startDate}`);
      urlParams.push(`endDate=${dates.endDate}`);
    }
    if (urlParams.length > 0) {
      url += '?' + urlParams.join('&');
    }
    return this.apiService.httpGet(
      url,
      false,
      {},
      {
        redirectOnForbidden: true,
      }
    );
  }

  public getTask(campaignId: string, workspaceId?: string): Observable<any> {
    let url = `/api/tasks`;
    const urlParams = [];
    if (campaignId) {
      urlParams.push(`campaignId=${campaignId}`);
    }
    if (workspaceId) {
      urlParams.push(`workspaceId=${workspaceId}`);
    }

    if (urlParams.length > 0) {
      url += '?' + urlParams.join('&');
    }
    return this.apiService.httpGet(url);
  }

  mapWorkspace(brandId, companyId, workspaces) {
    return this.apiService.httpPost(`/api/brands/${companyId}/${brandId}/add-workspaces`, workspaces);
  }

  /**
   *
   * @returns analytics data
   */
  getAnalyticTimelinesCampaigns(workspaceIds: string[]) {
    const url = `/analytics/timelines/campaign/users`;
    const requestBody = {
      allWorkspaces: workspaceIds?.length === 0,
      workspaces: workspaceIds, // Send the brand IDs or an empty array
    };
    return this.apiService.analyticsHttpPost(url, requestBody);
  }

  getAnalyticWorkspaces(brandIds: string[]) {
    const url = `/analytics/workspace/users`; // API endpoint
    const requestBody = {
      allBrands: brandIds?.length === 0,
      brands: brandIds, // Send the brand IDs or an empty array
    };
    return this.apiService.analyticsHttpPost(url, requestBody);
  }

  getAnalyticBrands() {
    return this.apiService.httpGet(`/analytics/brands/users`, true);
  }

  getAnalyticProjects() {
    return this.apiService.httpGet(`/analytics/projects/users`, true);
  }

  getTimelineOverView(): Observable<any> {
    return this.apiService.httpGet(`/analytics/timelines/overview`, true);
  }

  getTimelineTree(): Observable<any> {
    return this.apiService.httpGet(`/analytics/timelines/milestone/tree`, true);
  }

  getAggregate(params: any): Observable<any> {
    return this.apiService.analyticsHttpPost(`/analytics/timelines/aggregate`, params);
  }

  clearAnalyticsCache(): void {
    this.brandsCache.next([]);
    this.brandByIdCache.next({});
  }
}
